import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import SubcategoriesTemplate from "templates/lookups/subcategories";
import {
  activateCategoryRequest,
  createCategoriesRequest,
  deactivateCategoryRequest,
  deleteCategoryRequest,
  getCategoriesRequest,
  getCategoryDetailsRequest,
  updateCategoryRequest,
} from "services/modules/lookups/categories";

function SubcategoriesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categoryId } = useParams();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.categories.categories);
  const count = useSelector((state) => state.categories.count);
  const details = useSelector((state) => state.categories.category);
  const load = useSelector((state) => state.categories.load);

  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile,
  );
  const permissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions.categories,
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);
  const [parentName, setParentName] = useState("");

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getCategoryDetailsRequest({ category_id: id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        keyword: "",
        sort_by: "id",
        parent_id: categoryId,
        ...filterObj,
      },
    };
    dispatch(getCategoriesRequest(requestData));
  };

  const mappedData = list?.map((data) => {
    return {
      ...data,
      created_at: <>{dayjs(data?.created_at).format("DD-MM-YYYY hh:mm")}</>,
      updated_at: <>{dayjs(data?.updated_at).format("DD-MM-YYYY hh:mm")}</>,
      display_priority: data?.display_priority ?? "-",
      is_featured: <>{data?.is_featured ? t("common.yes") : t("common.no")}</>,
      is_active_label: (
        <>{data?.is_active ? t("common.yes") : t("common.no")}</>
      ),
    };
  });

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: true,
      path: "/categories",
      title: t("lookups_categories.title"),
    },
    {
      active: !type ? false : true,
      path: `/categories/${categoryId}/subcategories`,
      title: parentName || t("lookups_subcategories.list_title"),
    },
    type && {
      active: false,
      title: id
        ? t("lookups_subcategories.edit")
        : t("lookups_subcategories.add"),
    },
  ];

  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate(`/categories/${categoryId}/subcategories`);
      !id && formik?.resetForm();
      toast.success(
        id
          ? t("lookups_subcategories.updated")
          : t("lookups_subcategories.created"),
      );
      getList(filterObj);
    };
    const requestData = {
      body: {
        category: {
          ...formik?.values,
          image: data?.image,
          parent_id: Number(categoryId),
          display_priority: formik?.values?.display_priority
            ? Number(formik?.values?.display_priority)
            : null,
          is_featured: !!formik?.values?.is_featured,
          is_voucher: !!formik?.values?.is_voucher,
        },
      },
      action,
    };

    if (id) {
      dispatch(updateCategoryRequest({ ...requestData, id }));
    } else {
      dispatch(createCategoriesRequest(requestData));
    }
  };

  const filterList = [
    {
      type: "filter",
      placeholder: t("lookups_categories.vouchers"),
      onChange: (value) => {
        setFilterObj((s) => {
          return { ...s, is_voucher: value?.value, page_number: 1 };
        });
      },
      options: [
        { id: true, name: t("common.yes") },
        { id: false, name: t("common.no") },
      ],
    },
  ];

  const handleDelete = (deleteId) => {
    const action = () => {
      if (list.length === 1 && page !== 1) {
        setPage(page - 1);
        setFilterObj({
          ...filterObj,
          page_number: page - 1,
        });
        getList({ ...filterObj, page_number: page - 1 });
      } else {
        getList(filterObj);
      }
      type === "details" &&
        navigate(`/categories/${categoryId}/subcategories`);
      toast.success(t("lookups_subcategories.deleted"));
    };
    const requestData = {
      id: deleteId,
      action,
    };
    dispatch(deleteCategoryRequest(requestData));
    setOpenDeleteModal(false);
  };

  const handleToggleActive = (data) => {
    const isActive = data?.is_active === true;
    const action = () => {
      toast.success(
        isActive
          ? t("lookups_categories.deactivated")
          : t("lookups_categories.activated"),
      );
      getList(filterObj);
    };
    const requestData = { id: data?.id, action };
    if (isActive) {
      dispatch(deactivateCategoryRequest(requestData));
    } else {
      dispatch(activateCategoryRequest(requestData));
    }
  };

  const actionsList = ({ data }) => {
    const isActive = data?.is_active === true;
    return [
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_update;
        },
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
      },
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_update;
        },
        click: () => {
          handleToggleActive(data);
        },
        text: isActive
          ? t("lookups_categories.deactivate")
          : t("lookups_categories.activate"),
      },
      {
        condition: () => {
          return isSuperAdmin || permissions?.can_delete;
        },
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("common.delete"),
      },
    ];
  };

  const headerChildren = <></>;
  const modalComponents = [];

  useEffect(() => {
    if (!categoryId) return;
    dispatch(
      getCategoryDetailsRequest({
        category_id: categoryId,
        skipStore: true,
        action: (response) => {
          const parent = response?.data?.current_category;
          setParentName(
            parent?.localized_data?.en?.name || parent?.name || "",
          );
        },
      }),
    );
  }, [categoryId]);

  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj, categoryId]);

  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  return (
    <>
      <Loader open={load?.loading && load?.type == "list"} />
      <SubcategoriesTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={headerChildren}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={modalComponents}
        can_create={isSuperAdmin || permissions?.can_create}
      />
    </>
  );
}

export default SubcategoriesPage;
